import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
  Timestamp,
} from 'firebase/firestore'
import { db, isFirebaseConfigured } from './firebase'
import type { News, NewsCategory } from '../types'

const NEWS_COLLECTION = 'news'

// デモ用のサンプルデータ
const DEMO_NEWS: News[] = [
  {
    id: '1',
    title: '[デモ]2025年度第2回理事会の開催について',
    content: '<p>2025年度第2回理事会を開催いたします。</p>',
    category: 'announcement',
    publishDate: Timestamp.fromDate(new Date('2025-09-19')),
    expiryDate: null,
    isPublished: true,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    createdBy: 'system',
  },
  {
    id: '2',
    title: '[デモ]新規事業の開始について',
    content: '<p>新規事業を開始いたします。</p>',
    category: 'announcement',
    publishDate: Timestamp.fromDate(new Date('2025-09-15')),
    expiryDate: null,
    isPublished: true,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    createdBy: 'system',
  },
  {
    id: '3',
    title: '[デモ]会員向けセミナーの開催について',
    content: '<p>会員向けセミナーを開催いたします。</p>',
    category: 'event',
    publishDate: Timestamp.fromDate(new Date('2025-09-10')),
    expiryDate: null,
    isPublished: true,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    createdBy: 'system',
  },
  {
    id: '4',
    title: '[デモ]デジタル技術活用事業の進捗報告を掲載しました',
    content: '<p>デジタル技術活用事業の進捗を報告いたします。</p>',
    category: 'report',
    publishDate: Timestamp.fromDate(new Date('2025-09-18')),
    expiryDate: null,
    isPublished: true,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    createdBy: 'system',
  },
]

/**
 * 公開されているお知らせ一覧を取得
 */
export async function getPublishedNews(limitCount = 10): Promise<News[]> {
  const isDev = import.meta.env.DEV

  // Firebase が設定されていない場合は、開発環境のみデモデータを返す
  if (!isFirebaseConfigured || !db) {
    if (isDev) {
      console.log('📝 Using demo news data (Dev mode)')
      return DEMO_NEWS.slice(0, limitCount)
    }
    console.warn('⚠️ Firebase not configured')
    return []
  }

  try {
    const now = Timestamp.now()
    
    const q = query(
      collection(db, NEWS_COLLECTION),
      where('isPublished', '==', true),
      where('publishDate', '<=', now),
      orderBy('publishDate', 'desc'),
      limit(limitCount)
    )

    const snapshot = await getDocs(q)
    const newsList: News[] = []

    snapshot.forEach((doc) => {
      const data = doc.data()
      
      // 有効期限チェック
      if (!data.expiryDate || data.expiryDate > now) {
        newsList.push({
          id: doc.id,
          ...data,
        } as News)
      }
    })

    // データがない場合、開発環境のみデモデータを返す
    if (newsList.length === 0) {
      if (isDev) {
        console.log('📝 No news in Firestore, using demo data (Dev mode)')
        return DEMO_NEWS.slice(0, limitCount)
      }
      return []
    }

    return newsList
  } catch (error) {
    console.error('Error fetching news:', error)
    // エラー時、開発環境のみデモデータを返す
    if (isDev) {
      console.warn('⚠️ Failed to fetch news from Firestore, using demo data (Dev mode)')
      return DEMO_NEWS.slice(0, limitCount)
    }
    // 本番環境ではエラーを握りつぶし空配列を返す（コンソールには出力済み）
    return []
  }
}

/**
 * お知らせ詳細を取得
 */
export async function getNewsById(id: string): Promise<News | null> {
  const isDev = import.meta.env.DEV

  // Firebase が設定されていない場合は、開発環境のみデモデータから検索
  if (!isFirebaseConfigured || !db) {
    if (isDev) {
      console.log('📝 Using demo news data for ID (Dev mode):', id)
      return DEMO_NEWS.find((news) => news.id === id) || null
    }
    return null
  }

  try {
    const docRef = doc(db, NEWS_COLLECTION, id)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      // Firestoreになくてもデモデータにあればそれを返す（開発環境のみ）
      if (isDev) {
        const demoNews = DEMO_NEWS.find((news) => news.id === id)
        if (demoNews) {
          console.log('📝 News not found in Firestore, found in demo data (Dev mode)')
          return demoNews
        }
      }
      return null
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as News
  } catch (error) {
    console.error('Error fetching news detail:', error)
    // エラー時、開発環境のみデモデータをチェック
    if (isDev) {
      console.warn('⚠️ Failed to fetch news detail from Firestore, checking demo data (Dev mode)')
      return DEMO_NEWS.find((news) => news.id === id) || null
    }
    // 本番環境ではエラーを握りつぶしnullを返す（コンソールには出力済み）
    return null
  }
}

/**
 * 日付を表示用にフォーマット
 */
export function formatDate(timestamp: Timestamp): string {
  const date = timestamp.toDate()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  
  return `${year}年${month}月${day}日`
}

/**
 * カテゴリー表示名
 */
export const NEWS_CATEGORY_LABELS: Record<NewsCategory, string> = {
  announcement: 'お知らせ',
  event: 'イベント',
  report: '事業報告',
}
